import type { AddressUpdateNote } from 'src/components/pages/info/document/index/types'

type Translate = (_text: string) => string

type AddressListItem = string | {
  type: 'address'
  prefix: string
  variant: 'legal' | 'actual'
} | AddressUpdateNote & { type: 'update' }

export function getPdAddressItems(staticPath: string, translate: Translate): AddressListItem[] {
  const pdfBase = `${staticPath}_v1/pd/documents/pdf`

  return [
    translate('ООО «МЕДРОКЕТ»'),
    translate('Директор Федосов Сергей Ростиславович (действует на основании Устава)'),
    translate('ОГРН 1122311003760'),
    translate('ИНН 2311144947'),
    translate('КПП 231001001'),
    {
      type: 'address',
      prefix: translate('Юридический адрес:'),
      variant: 'legal',
    },
    {
      type: 'address',
      prefix: translate('Фактический адрес:'),
      variant: 'actual',
    },
    translate('Почтовый адрес: 350000, г Краснодар, ул. Рашпилевская, д. 60, а/я 3272'),
    translate('р/с № 40702810226020000870 в Филиале «Ростовский» АО «АЛЬФА-БАНК» г.Ростов-на-Дону'),
    translate('к/с № 30101810500000000207'),
    translate('БИК 046015207'),
    {
      type: 'update',
      text: translate('UPD. 27.06.2019 изменился юридический адрес с «г. Краснодар, ул. им Фелицына Е.Д., д.8» на «г. Краснодар, ул. Северная, д. 327, офис 801», а также КПП с 231101001 на 231001001. Документы смены юридического адреса - '),
      links: [
        {
          label: translate('Выписка из ЕГРЮЛ смена адреса'),
          href: `${pdfBase}/Выписка_из_ЕГРЮЛ_-_смена_адреса.pdf`,
        },
        {
          label: translate('Лист записи о изменении адреса'),
          href: `${pdfBase}/Лист_записи_о_изменении_адреса.pdf`,
        },
      ],
      suffix: '.',
    },
    {
      type: 'update',
      text: translate('UPD. 07.02.2022 изменился юридический адрес с «г. Краснодар, ул. Северная, д. 327, офис 801» на «г. Краснодар, ул. Буденного, д. 182, офис 87». Документы смены юридического адреса - '),
      links: [
        {
          label: translate('Выписка из ЕГРЮЛ смена адреса'),
          href: `${pdfBase}/Выписка_из_ЕГРЮЛ_-_смена_адреса_v2.pdf`,
        },
        {
          label: translate('Лист записи о изменении адреса'),
          href: `${pdfBase}/Лист_записи_о_изменении_адреса_v2.pdf`,
        },
      ],
      suffix: '.',
    },
  ]
}

export function getByAddressItems(translate: Translate): AddressListItem[] {
  return [
    translate('Филиал общества с ограниченной ответственностью «МЕДРОКЕТ» в Республике Беларусь'),
    translate('Директор Филиала Эскендиров Халим Мурзалиевич (Действует на основании Доверенности от 21.03.2025 г. № 23АВ6216419, зарегистрировано в реестре № 32/31-н/23-2025-5-288.)'),
    translate('УНП 102418021'),
    translate('Юридический адрес: Республика Беларусь, г. Минск, Пер. Можайского 3-й, дом 11, пом. 109'),
    translate('р/с BY15 BPSB 3021 1939 7001 2933 0000 в ОАО «СБЕР БАНК» Г.МИНСК'),
    translate('БИК BPSBBY2X'),
  ]
}

export function getKzAddressItems(translate: Translate): AddressListItem[] {
  return [
    translate('Филиал общества с ограниченной ответственностью «МЕДРОКЕТ» в Республике Казахстан'),
    translate('Директор Филиала Эскендиров Халим Мурзалиевич (Действует на основании Доверенности)'),
    translate('БИН 250241013133'),
    translate('Юридический адрес: 010000,Республика Казахстан, город Астана, район Сарыарка, улица Тарас Шевченко, здание 4/1, н.п. 17'),
    translate('Почтовый адрес: Z11A3X4,Республика Казахстан, город Астана, район Сарыарка, улица Тарас Шевченко, здание 4/1, офис 507'),
    translate('р/с KZ18914012203KZ00J31(Тенге) в АО «Bereke Bank»'),
    translate('БИК BRKEKZKA'),
  ]
}

export function getKgAddressItems(translate: Translate): AddressListItem[] {
  return [
    translate('Филиал общества с ограниченной ответственностью «МЕДРОКЕТ» в Республике Кыргызстан'),
    translate('Директор Филиала Эскендиров Халим Мурзалиевич (Действует на основании Доверенности)'),
    translate('ИНН 02703202510097'),
    translate('Юридический адрес: Кыргызская Республика, Бишкек, Октябрьский район, мкрн.6, 34, 88'),
    translate('р/с № 1290043130804432 (в сомах) в ОАО «Элдик Банк»'),
    translate('БИК 129004'),
  ]
}

export function getAddressTitle(countryIso: string, translate: Translate): string {
  if (countryIso === 'BY') {
    return translate('Юридическое лицо в Беларуси')
  }

  if (countryIso === 'KZ') {
    return translate('Юридическое лицо в Казахстане')
  }

  if (countryIso === 'KG') {
    return translate('Юридическое лицо в Кыргызстане')
  }

  return translate('Реквизиты компании')
}

export function getAddressItems(
  staticPath: string,
  countryIso: string,
  translate: Translate,
): AddressListItem[] {
  if (countryIso === 'BY') {
    return getByAddressItems(translate)
  }

  if (countryIso === 'KZ') {
    return getKzAddressItems(translate)
  }

  if (countryIso === 'KG') {
    return getKgAddressItems(translate)
  }

  return getPdAddressItems(staticPath, translate)
}
